import { __awaiter, __generator, __read } from "tslib";
import { fetchCapabilities } from '@office-start/capabilities-service';
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { fetchSharedContext } from '@office-start/context-service';
import { FileType } from '@office-start/document-types';
import { fetchFeatureGates } from '@office-start/feature-gates-service';
import { isWacFileType } from '@office-start/file-utility';
import { fetchMyDriveRootUrls } from '@office-start/graph-service';
import { fetchIdentity } from '@office-start/identity-service';
import { mapWorkloadToHarmonySessionSource } from '@office-start/instrumentation-service';
import { HarmonySessionKey, HarmonySessionSourceKey, } from '@office-start/instrumentation-types';
import { OfficeWorkload } from '@office-start/officehome-types';
import { fetchTemplatesConfig } from '@office-start/templates-config-service';
import { fetchUserEntitlements } from '@office-start/user-entitlements-service';
import { fetchWorkloads } from '@office-start/workloads-service';
import { appsNotSupporting0ByteFile, } from '../models';
import { closeNewTab } from '../util/closeNewTab';
import { defaultCreateErrorCallback } from '../util/defaultCreateErrorCallback';
import { getNewDocLinkTarget } from '../util/getNewDocLinkTarget';
import { createDocAspx } from './createDocAspx';
import { createNewClipchamp } from './createNewClipchamp';
import { createNewForm } from './createNewForm';
import { createNewStream } from './createNewStream';
import { createNewSway } from './createNewSway';
import { createOneDriveFile } from './createOneDriveFile';
import { getLicenseStatus } from './getLicenseStatus';
import { launchAppWrapper } from './launchAppWrapper';
import { launchOneNoteDialog } from './launchOneNoteDialog';
import { launchTemplateWrapper } from './launchTemplateWrapper';
import { launchUrlWrapper } from './launchUrlWrapper';
export function createItem(_a) {
    var _b;
    var app = _a.app, candidate = _a.candidate, getOrUpdatePendingCreates = _a.getOrUpdatePendingCreates, _c = _a.createLink, createLink = _c === void 0 ? undefined : _c, _d = _a.template, template = _d === void 0 ? undefined : _d, _e = _a.instrumentClick, instrumentClick = _e === void 0 ? undefined : _e, _f = _a.errorCallback, errorCallback = _f === void 0 ? defaultCreateErrorCallback : _f, _g = _a._fallbackUrl, _fallbackUrl = _g === void 0 ? undefined : _g, _h = _a.enterpriseDriveId, enterpriseDriveId = _h === void 0 ? undefined : _h, _j = _a.linkTarget, linkTarget = _j === void 0 ? undefined : _j, _k = _a.area, area = _k === void 0 ? undefined : _k, lastFocusedHtmlElement = _a.lastFocusedHtmlElement, prefetchedToken = _a.prefetchedToken;
    return __awaiter(this, void 0, void 0, function () {
        var capabilities, targetTab, shouldOpenInUIHost, newDocLinkTarget, _l, componentContext, templatesConfig, sharedContext, identity, myDriveRootUrls, ShellWorkloads, userEntitlements, featureGates, referralParams, createOptions, shouldUseDocAspxFlow, isLicensed, startTime, resultLogged, result, _m, instrumentCallback, url, executeErrorCallback;
        return __generator(this, function (_o) {
            switch (_o.label) {
                case 0: return [4 /*yield*/, fetchCapabilities()];
                case 1:
                    capabilities = _o.sent();
                    targetTab = null;
                    if (!capabilities) {
                        instrumentClick &&
                            instrumentClick("Error" /* CreateDocumentResultType.Error */, createLink, template, 0);
                        return [2 /*return*/, "Error" /* CreateDocumentResultType.Error */];
                    }
                    shouldOpenInUIHost = capabilities.wacHostEnabled &&
                        capabilities.wacHostApps &&
                        capabilities.wacHostApps.has(app) &&
                        (!enterpriseDriveId || capabilities.wacHostOrgTemplatesEnabled);
                    newDocLinkTarget = getNewDocLinkTarget(app, shouldOpenInUIHost, linkTarget);
                    // Open a new tab
                    if (!shouldOpenInUIHost &&
                        !capabilities.pwaEnabled &&
                        app !== FileType.OneNote) {
                        targetTab = window.open('', newDocLinkTarget);
                    }
                    return [4 /*yield*/, Promise.all([
                            fetchOfficeComponentContext(),
                            fetchTemplatesConfig(),
                            fetchSharedContext(),
                            fetchIdentity(),
                            fetchMyDriveRootUrls(),
                            fetchWorkloads(),
                        ]).then()];
                case 2:
                    _l = __read.apply(void 0, [_o.sent(), 6]), componentContext = _l[0], templatesConfig = _l[1], sharedContext = _l[2], identity = _l[3], myDriveRootUrls = _l[4], ShellWorkloads = _l[5];
                    return [4 /*yield*/, fetchUserEntitlements()];
                case 3:
                    userEntitlements = (_o.sent()).result;
                    return [4 /*yield*/, fetchFeatureGates()];
                case 4:
                    featureGates = (_o.sent()).result;
                    if (!app || !candidate || !sharedContext || !componentContext) {
                        instrumentClick &&
                            instrumentClick("Error" /* CreateDocumentResultType.Error */, createLink, template, 0);
                        return [2 /*return*/, "Error" /* CreateDocumentResultType.Error */];
                    }
                    referralParams = {};
                    if (isWacFileType(app)) {
                        referralParams = {};
                        referralParams[HarmonySessionSourceKey] =
                            mapWorkloadToHarmonySessionSource(componentContext.workload);
                        referralParams[HarmonySessionKey] = componentContext.sessionId;
                    }
                    createOptions = {
                        getOrUpdatePendingCreates: getOrUpdatePendingCreates,
                        componentContext: componentContext,
                        capabilities: capabilities,
                        templatesConfig: templatesConfig,
                        sharedContext: sharedContext,
                        identity: identity,
                        myDriveRootUrls: myDriveRootUrls === null || myDriveRootUrls === void 0 ? void 0 : myDriveRootUrls.result,
                        referralParams: referralParams,
                        userEntitlements: userEntitlements,
                        targetTab: targetTab,
                        newDocLinkTarget: newDocLinkTarget,
                        featureGates: featureGates,
                    };
                    shouldUseDocAspxFlow = candidate.createDocAspxEnabled &&
                        !shouldOpenInUIHost &&
                        (componentContext.workload === OfficeWorkload.OfficeHome ||
                            componentContext.workload === OfficeWorkload.OfficeHomeReact);
                    return [4 /*yield*/, getLicenseStatus(app, ShellWorkloads, capabilities.scenarioId)];
                case 5:
                    isLicensed = _o.sent();
                    if (newDocLinkTarget === '_self' && getOrUpdatePendingCreates() > 0) {
                        return [2 /*return*/, "CreatePending" /* CreateDocumentResultType.CreatePending */];
                    }
                    startTime = Date.now();
                    resultLogged = false;
                    result = {
                        type: "CreatePending" /* CreateDocumentResultType.CreatePending */,
                    };
                    getOrUpdatePendingCreates(1);
                    _m = app;
                    switch (_m) {
                        case FileType.Excel: return [3 /*break*/, 6];
                        case FileType.Word: return [3 /*break*/, 6];
                        case FileType.PowerPoint: return [3 /*break*/, 6];
                        case FileType.Visio: return [3 /*break*/, 6];
                        case FileType.OneNote: return [3 /*break*/, 18];
                        case FileType.OneNotePage: return [3 /*break*/, 20];
                        case FileType.Sway: return [3 /*break*/, 21];
                        case FileType.Forms: return [3 /*break*/, 23];
                        case FileType.FormsQuiz: return [3 /*break*/, 23];
                        case FileType.Fluid: return [3 /*break*/, 25];
                        case FileType.FluidActivityBlueprint: return [3 /*break*/, 25];
                        case FileType.Stream: return [3 /*break*/, 26];
                        case FileType.ScreenRecording: return [3 /*break*/, 26];
                        case FileType.PlayLists: return [3 /*break*/, 28];
                        case FileType.PlayList: return [3 /*break*/, 28];
                        case FileType.Clipchamp: return [3 /*break*/, 29];
                        case FileType.Designer: return [3 /*break*/, 31];
                    }
                    return [3 /*break*/, 33];
                case 6:
                    if (!isLicensed) return [3 /*break*/, 16];
                    if (!capabilities.pwaEnabled) return [3 /*break*/, 11];
                    if (!(!template && appsNotSupporting0ByteFile.includes(app))) return [3 /*break*/, 8];
                    return [4 /*yield*/, launchAppWrapper(app)];
                case 7:
                    // When users click create new workbook on HWA start page, we don't create new file since Excel or Visio desktop app cannot open 0-byte files.
                    result = _o.sent();
                    return [3 /*break*/, 10];
                case 8:
                    if (!template) return [3 /*break*/, 10];
                    return [4 /*yield*/, launchTemplateWrapper(app, (_b = template === null || template === void 0 ? void 0 : template.TemplateBinaryUrl) !== null && _b !== void 0 ? _b : '')];
                case 9:
                    // When creating new files with template in HWA, we will try use protocol handler to open the file with template url provided.
                    // This does not require a new file being created first.
                    result = _o.sent();
                    _o.label = 10;
                case 10: return [3 /*break*/, 13];
                case 11:
                    if (!shouldUseDocAspxFlow) return [3 /*break*/, 13];
                    instrumentCallback = function (processTimeInMilliseconds) {
                        instrumentClick &&
                            instrumentClick("GoToBlankDocInstant" /* CreateDocumentResultType.GoToBlankDocInstant */, createLink, template, processTimeInMilliseconds);
                    };
                    return [4 /*yield*/, createDocAspx({
                            candidate: candidate,
                            options: createOptions,
                            startTime: startTime,
                            template: template,
                            instrumentCallback: instrumentCallback,
                            enterpriseDriveId: enterpriseDriveId,
                            prefetchedToken: prefetchedToken,
                        })];
                case 12:
                    result = _o.sent();
                    if (result.type !== "Error" /* CreateDocumentResultType.Error */) {
                        resultLogged = true;
                    }
                    _o.label = 13;
                case 13:
                    if (!(result.type === "CreatePending" /* CreateDocumentResultType.CreatePending */ ||
                        result.type === "OpenNewDocFailed" /* CreateDocumentResultType.OpenNewDocFailed */ ||
                        result.type === "Error" /* CreateDocumentResultType.Error */)) return [3 /*break*/, 15];
                    return [4 /*yield*/, createOneDriveFile(app, candidate, createOptions, startTime, template, enterpriseDriveId, shouldOpenInUIHost)];
                case 14:
                    // TODO: instead of starting OneDrive create flow, show error dialog if the failure is due to token issue
                    // Use OneDrive create flow if previous two handlings don't apply or failed
                    result = _o.sent();
                    _o.label = 15;
                case 15: return [3 /*break*/, 17];
                case 16:
                    // TODO: Return license error instead of a generic error
                    result = { type: "Error" /* CreateDocumentResultType.Error */ };
                    _o.label = 17;
                case 17: return [3 /*break*/, 34];
                case 18: return [4 /*yield*/, launchOneNoteDialog(lastFocusedHtmlElement)];
                case 19:
                    result = _o.sent();
                    return [3 /*break*/, 34];
                case 20: return [3 /*break*/, 34];
                case 21: return [4 /*yield*/, createNewSway(candidate, createOptions, createLink)];
                case 22:
                    result = _o.sent();
                    return [3 /*break*/, 34];
                case 23: return [4 /*yield*/, createNewForm(app, candidate, createOptions, area, template)];
                case 24:
                    result = _o.sent();
                    return [3 /*break*/, 34];
                case 25: return [3 /*break*/, 34];
                case 26: return [4 /*yield*/, createNewStream(app, createOptions, area)];
                case 27:
                    result = _o.sent();
                    return [3 /*break*/, 34];
                case 28: 
                // TODO
                return [3 /*break*/, 34];
                case 29: return [4 /*yield*/, createNewClipchamp(createOptions, area, template)];
                case 30:
                    result = _o.sent();
                    return [3 /*break*/, 34];
                case 31:
                    if (!template) return [3 /*break*/, 33];
                    url = "https://designer.microsoft.com/?templateId=".concat(template.AssetId, "&correlationId=").concat(componentContext.correlationId, "&referrer=harmony&utm_source=harmony&utm_medium=").concat(componentContext.workload, "&utm_campaign=template");
                    return [4 /*yield*/, launchUrlWrapper(url, targetTab, newDocLinkTarget)];
                case 32:
                    result = _o.sent();
                    _o.label = 33;
                case 33: return [3 /*break*/, 34];
                case 34:
                    getOrUpdatePendingCreates(-1);
                    // Log `CreatePending` before returning as `Error`
                    !resultLogged &&
                        instrumentClick &&
                        instrumentClick(result.type, createLink, template);
                    result =
                        result.type !== "CreatePending" /* CreateDocumentResultType.CreatePending */
                            ? result
                            : { type: "Error" /* CreateDocumentResultType.Error */ };
                    executeErrorCallback = result.type === "Error" /* CreateDocumentResultType.Error */ ||
                        result.type === "OpenNewDocFailed" /* CreateDocumentResultType.OpenNewDocFailed */ ||
                        result.type === "OpenBlockedByPopupBlocker" /* CreateDocumentResultType.OpenBlockedByPopupBlocker */;
                    if (executeErrorCallback) {
                        closeNewTab(targetTab, newDocLinkTarget);
                    }
                    if (errorCallback && executeErrorCallback) {
                        errorCallback({
                            resultType: result.type,
                            fileType: app,
                            documentWebUrl: result.documentWebUrl,
                        });
                    }
                    return [2 /*return*/, result.type];
            }
        });
    });
}
//# sourceMappingURL=createItem.js.map